import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getApiBase, withPartNumberSkuQuery } from "../helpers/helpers";
import { withIDBFallback, IDB_STORES, IDB_TTL } from "./IDBCacheService";

const YEARS_CACHE_TIME = 1000 * 60 * 30; // 30 minutes
const YEARS_STALE_TIME = 1000 * 60 * 15; // 15 minutes

export const getType = (): string => {
  if (window?.is_real_truck) return "realtruck";
  if (window?.scs_force_custom) return "custom";
  if (window?.scs_custom_endpoint) return "custom_endpoint";
  return "normal";
};

export const getYearsQueryKey = () => ["years", getType()];

export const fetchYears = async () => {
  const type = getType();
  let query = `?type=${type}`;
  if (window.scs_use_optgroup) {
    query += `&with_sales=yes`;
  }

  const idbKey = `years_${type}${window.scs_use_optgroup ? "_with_sales" : ""}`;

  return withIDBFallback(
    IDB_STORES.YEARS,
    idbKey,
    IDB_TTL.YEARS,
    async () => {
      const urlYears = withPartNumberSkuQuery(`${getApiBase()}/api/v1/years` + query);
      const response = await fetch(urlYears, {
        mode: "cors",
        headers: {
          "Cache-Control": "public, max-age=1800" // 30 minutes browser cache
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch years: ${response.statusText}`);
      }

      const data = await response.json();
      return data.years;
    }
  );
};

// Hook for using years data
export const useYearsQuery = () => {
  return useQuery({
    queryKey: getYearsQueryKey(),
    queryFn: fetchYears,
    staleTime: YEARS_STALE_TIME,
    cacheTime: YEARS_CACHE_TIME,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

// Prefetch function for early loading
export const prefetchYears = async (queryClient: any) => {
  await queryClient.prefetchQuery({
    queryKey: getYearsQueryKey(),
    queryFn: fetchYears,
    staleTime: YEARS_STALE_TIME,
    cacheTime: YEARS_CACHE_TIME,
  });
};

// Check if years data is already cached
export const isYearsCached = (queryClient: any) => {
  return queryClient.getQueryData(getYearsQueryKey()) !== undefined;
};